import { Observable } from '@backstage/types';
import ObservableImpl from 'zen-observable';

export class PublishSubject<T>
  implements Observable<T>, ZenObservable.SubscriptionObserver<T>
{
  private isClosed = false;
  private terminatingError?: Error;

  private readonly observable = new ObservableImpl<T>(subscriber => {
    if (this.isClosed) {
      if (this.terminatingError) {
        subscriber.error(this.terminatingError);
      } else {
        subscriber.complete();
      }
      return () => {};
    }

    this.subscribers.add(subscriber);
    return () => {
      this.subscribers.delete(subscriber);
    };
  });

  private readonly subscribers = new Set<
    ZenObservable.SubscriptionObserver<T>
  >();

  [Symbol.observable]() {
    return this;
  }

  get closed() {
    return this.isClosed;
  }

  next(value: T) {
    if (this.isClosed) {
      throw new Error('PublishSubject is closed');
    }
    this.subscribers.forEach(subscriber => subscriber.next(value));
  }

  error(error: Error) {
    if (this.isClosed) {
      throw new Error('PublishSubject is closed');
    }
    this.isClosed = true;
    this.terminatingError = error;
    this.subscribers.forEach(subscriber => subscriber.error(error));
  }

  complete() {
    if (this.isClosed) {
      throw new Error('PublishSubject is closed');
    }
    this.isClosed = true;
    this.subscribers.forEach(subscriber => subscriber.complete());
  }

  subscribe(observer: ZenObservable.Observer<T>): ZenObservable.Subscription;
  subscribe(
    onNext: (value: T) => void,
    onError?: (error: any) => void,
    onComplete?: () => void,
  ): ZenObservable.Subscription;
  subscribe(
    onNext: ZenObservable.Observer<T> | ((value: T) => void),
    onError?: (error: any) => void,
    onComplete?: () => void,
  ): ZenObservable.Subscription {
    const observer =
      typeof onNext === 'function'
        ? {
            next: onNext,
            error: onError,
            complete: onComplete,
          }
        : onNext;

    return this.observable.subscribe(observer);
  }
}
